import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Autocomplete, AutocompleteItem, Button, Spinner } from '@structural-climbing/ui'
import type { DeskCandidate, DeskMatch } from './DeskMatchBlock'

interface Props {
  loadCandidates: () => Promise<DeskCandidate[] | null>
  loadMember: (userId: string) => Promise<DeskMatch | null>
  onPicked: (match: DeskMatch) => void
}

/**
 * For a request the verified-email match found nobody for: lets staff link it
 * to someone they added at the desk with no email, a phone-only entry. Closed
 * until asked for, so an ordinary approval stays as it was. The Worker lists
 * only those desk members and checks the pick again on approve.
 */
export function DeskMemberPicker({ loadCandidates, loadMember, onPicked }: Props) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [candidates, setCandidates] = useState<DeskCandidate[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  const label = (c: DeskCandidate) =>
    [
      c.name,
      c.phone && (c.same_phone ? `${c.phone} (${t('requests.deskLinkSamePhone')})` : c.phone),
      t('requests.deskMatchAdded', { date: date(c.added_at) }),
    ]
      .filter(Boolean)
      .join(' · ')

  async function openPicker() {
    setOpen(true)
    setLoading(true)
    setFailed(false)
    const rows = await loadCandidates().catch(() => null)
    setLoading(false)
    if (!rows) setFailed(true)
    setCandidates(rows ?? [])
  }

  async function pick(userId: string) {
    setLoading(true)
    setFailed(false)
    const match = await loadMember(userId).catch(() => null)
    setLoading(false)
    if (match) onPicked(match)
    else setFailed(true)
  }

  if (!open)
    return (
      <Button size="sm" variant="light" className="self-start" onPress={() => void openPicker()}>
        {t('requests.deskLinkOpen')}
      </Button>
    )

  return (
    <div className="flex flex-col gap-2">
      {loading && <Spinner size="sm" className="self-start" />}
      {failed && <p className="text-sm text-danger">{t('common.errorDefault')}</p>}
      {candidates && candidates.length === 0 && !loading && !failed && (
        <p className="text-sm text-foreground/60">{t('requests.deskLinkEmpty')}</p>
      )}
      {candidates && candidates.length > 0 && (
        <Autocomplete
          label={t('requests.deskLinkLabel')}
          isDisabled={loading}
          onSelectionChange={(key) => {
            if (key) void pick(String(key))
          }}
        >
          {candidates.map((c) => (
            <AutocompleteItem key={c.user_id} textValue={label(c)}>
              {label(c)}
            </AutocompleteItem>
          ))}
        </Autocomplete>
      )}
    </div>
  )
}
